import { Text } from '@fluentui/react-components';
import { CalendarAgendaRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../Communication/CommunicationMessage';
import { WidgetsHeadline } from '../WidgetsHeadline/WidgetsHeadline';
import './TimetableWidget.scss';
import { TimetableWidgetSkeleton } from './TimetableWidgetSkeleton';

export interface TimetableEvent {
  id?: string;
  title?: string;
  start?: string;
  end?: string;
  borderColor?: string;
  backgroundColor?: string;
  textColor?: string;
  extendedProps?: Record<string, unknown>;
}

export interface TimetableWidgetProps {
  isLoading: boolean;
  events: TimetableEvent[];
  renderEvent: (event: TimetableEvent, index: number) => ReactNode;
  error?: Error | null;
  onRetry?: () => void;
  classNames?: string;
  useDarkIllustration?: boolean;
}

export const TimetableWidget: FC<TimetableWidgetProps> = ({
  isLoading,
  events,
  renderEvent,
  error,
  onRetry,
  classNames,
  useDarkIllustration = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-cy="timetableWidget"
      className={cn('timetableWidget', 'flex', 'flex-col', 'items-stretch', classNames)}
    >
      <WidgetsHeadline
        content={t('dashboard:my_day_header', { defaultValue: 'Mein Tag' })}
        icon={<CalendarAgendaRegular fontSize={24} />}
      />
      {isLoading && <TimetableWidgetSkeleton />}
      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Text className="text-error">
            {t('dashboard:my_day_load_error', { defaultValue: 'Fehler beim Laden' })}
          </Text>
          {onRetry && (
            <button onClick={onRetry} className="btn-retry">
              {t('dashboard:my_day_load_error_reload', { defaultValue: 'Neu laden' })}
            </button>
          )}
        </div>
      )}
      {!isLoading && !error && (
        <div className="eventsWrapper">
          {events.length === 0 && (
            <CommunicationMessage
              illustration={Illustrations.Empty}
              headline=""
              text={t('dashboard:my_day_empty', { defaultValue: 'Heute keine Termine' })}
              illustrationSize={IllustrationSize.Small}
              useDarkIllustration={useDarkIllustration}
            />
          )}
          {events.map((event, index) => (
            <div
              key={event.id || index}
              className="event"
              style={{
                borderLeftColor: event.borderColor,
              }}
            >
              {renderEvent(event, index)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
